import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Tab, Tabs, Stack, MenuItem, TextField, Chip, Button, Dialog,
  DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { EmojiEvents, Add, Publish, StopCircle, ArrowBack } from '@mui/icons-material';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import EnhancedDataGrid from '../../components/ui/EnhancedDataGrid';
import DataTableCard from '../../components/ui/DataTableCard';
import api from '../../services/api';
import { formatMoney } from '../../utils/currency';
import { useTenantCurrency } from '../../hooks/useTenantCurrency';

type Period = 'week' | 'month' | 'all';
type Metric = 'trips' | 'earnings' | 'rating';

type LeaderboardRow = {
  rank: number;
  driverId: string;
  name: string;
  phone?: string;
  vehicleNumber?: string;
  rating: number;
  trips: number;
  earnings: number;
};

type Campaign = {
  _id: string;
  title: string;
  description?: string;
  campaignType: string;
  targetTrips?: number;
  targetEarnings?: number;
  bonusAmount: number;
  startsAt: string;
  endsAt: string;
  status: string;
};

const STATUS_COLOR: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  draft: 'default',
  active: 'success',
  paused: 'warning',
  ended: 'error',
};

const defaultCampaignForm = () => {
  const start = new Date();
  const end = new Date();
  end.setDate(end.getDate() + 7);
  return {
    title: '',
    description: '',
    campaignType: 'trip_target',
    targetTrips: 10,
    targetEarnings: 2000,
    bonusAmount: 500,
    startsAt: start.toISOString().slice(0, 16),
    endsAt: end.toISOString().slice(0, 16),
    notifyOnPublish: true,
  };
};

export default function DriverPerformancePage() {
  const { fieldLabel } = useTenantCurrency();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState(0);
  const [period, setPeriod] = useState<Period>('week');
  const [metric, setMetric] = useState<Metric>('trips');
  const [campaignOpen, setCampaignOpen] = useState(false);
  const [form, setForm] = useState(defaultCampaignForm);

  const { data: leaderboard, isLoading: lbLoading } = useQuery({
    queryKey: ['admin-leaderboard', period, metric],
    queryFn: async () => {
      const res = await api.get('/pricing/earnings/leaderboard', { params: { period, metric, limit: 50 } });
      return res.data.data as { rows: LeaderboardRow[]; updatedAt: string };
    },
    enabled: tab === 0,
  });

  const { data: campaigns, isLoading: campLoading } = useQuery({
    queryKey: ['incentive-campaigns'],
    queryFn: async () => {
      const res = await api.get('/pricing/earnings/incentive-campaigns', { params: { limit: 50 } });
      const payload = res.data.data;
      return (Array.isArray(payload) ? payload : payload?.rows ?? []) as Campaign[];
    },
    enabled: tab === 1,
  });

  const createCampaign = useMutation({
    mutationFn: async () => api.post('/pricing/earnings/incentive-campaigns', {
      ...form,
      targetTrips: form.campaignType === 'earnings_target' ? undefined : Number(form.targetTrips),
      targetEarnings: form.campaignType === 'earnings_target' ? Number(form.targetEarnings) : undefined,
      bonusAmount: Number(form.bonusAmount),
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['incentive-campaigns'] });
      setCampaignOpen(false);
      setForm(defaultCampaignForm());
    },
  });

  const publishCampaign = useMutation({
    mutationFn: (id: string) => api.post(`/pricing/earnings/incentive-campaigns/${id}/publish`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['incentive-campaigns'] }),
  });

  const endCampaign = useMutation({
    mutationFn: (id: string) => api.post(`/pricing/earnings/incentive-campaigns/${id}/end`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['incentive-campaigns'] }),
  });

  const lbColumns: GridColDef[] = [
    {
      field: 'rank', headerName: '#', width: 60,
      renderCell: (p) => (
        <Chip
          size="small"
          label={p.value}
          color={p.value <= 3 ? 'primary' : 'default'}
          icon={p.value === 1 ? <EmojiEvents fontSize="small" /> : undefined}
        />
      ),
    },
    { field: 'name', headerName: 'Driver', flex: 1, minWidth: 160 },
    { field: 'vehicleNumber', headerName: 'Vehicle', width: 120 },
    { field: 'trips', headerName: 'Trips', width: 90 },
    { field: 'earnings', headerName: 'Earnings', width: 110, valueGetter: (_v, row) => `${formatMoney(row.earnings, row)}` },
    { field: 'rating', headerName: 'Rating', width: 90, valueGetter: (_v, row) => row.rating?.toFixed?.(1) ?? row.rating },
    { field: 'phone', headerName: 'Phone', width: 130 },
  ];

  const campColumns: GridColDef[] = [
    { field: 'title', headerName: 'Campaign', flex: 1, minWidth: 180 },
    { field: 'campaignType', headerName: 'Type', width: 130 },
    {
      field: 'target', headerName: 'Target', width: 120,
      valueGetter: (_v, row: Campaign) =>
        row.campaignType === 'earnings_target' ? `${formatMoney(row.targetEarnings, row)}` : `${row.targetTrips} trips`,
    },
    { field: 'bonusAmount', headerName: 'Bonus', width: 90, valueGetter: (_v, row) => `${formatMoney(row.bonusAmount, row)}` },
    {
      field: 'status', headerName: 'Status', width: 100,
      renderCell: (p) => <Chip size="small" label={p.value} color={STATUS_COLOR[String(p.value)] || 'default'} />,
    },
    {
      field: 'period', headerName: 'Period', flex: 1, minWidth: 200,
      valueGetter: (_v, row: Campaign) =>
        `${new Date(row.startsAt).toLocaleDateString()} – ${new Date(row.endsAt).toLocaleDateString()}`,
    },
    {
      field: 'actions', type: 'actions', headerName: 'Actions', width: 120,
      getActions: (p) => {
        const row = p.row as Campaign;
        const actions = [];
        if (row.status === 'draft') {
          actions.push(
            <GridActionsCellItem
              key="publish"
              icon={<Publish />}
              label="Publish"
              onClick={() => publishCampaign.mutate(row._id)}
            />,
          );
        }
        if (row.status === 'active' || row.status === 'paused') {
          actions.push(
            <GridActionsCellItem
              key="end"
              icon={<StopCircle />}
              label="End"
              onClick={() => endCampaign.mutate(row._id)}
            />,
          );
        }
        return actions;
      },
    },
  ];

  return (
    <PageShell>
      <PageHeader
        title="Driver Performance"
        subtitle="Leaderboard rankings and incentive campaigns"
        actions={
          <Stack direction="row" spacing={1}>
            <Button component={RouterLink} to="/transport/drivers" startIcon={<ArrowBack />} variant="outlined">
              Drivers
            </Button>
            {tab === 1 && (
              <Button variant="contained" startIcon={<Add />} onClick={() => setCampaignOpen(true)}>
                New campaign
              </Button>
            )}
          </Stack>
        }
      />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Leaderboard" />
        <Tab label="Incentive campaigns" />
      </Tabs>

      {tab === 0 && (
        <Box>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
            <TextField select size="small" label="Period" value={period} onChange={(e) => setPeriod(e.target.value as Period)} sx={{ minWidth: 140 }}>
              <MenuItem value="week">This week</MenuItem>
              <MenuItem value="month">This month</MenuItem>
              <MenuItem value="all">All time</MenuItem>
            </TextField>
            <TextField select size="small" label="Rank by" value={metric} onChange={(e) => setMetric(e.target.value as Metric)} sx={{ minWidth: 140 }}>
              <MenuItem value="trips">Trips</MenuItem>
              <MenuItem value="earnings">Earnings</MenuItem>
              <MenuItem value="rating">Rating</MenuItem>
            </TextField>
          </Stack>
          <DataTableCard title="Driver leaderboard">
            <EnhancedDataGrid
              rows={leaderboard?.rows || []}
              columns={lbColumns}
              loading={lbLoading}
              getRowId={(r) => (r as LeaderboardRow).driverId}
              autoHeight
            />
          </DataTableCard>
        </Box>
      )}

      {tab === 1 && (
        <DataTableCard title="Incentive campaigns">
          <EnhancedDataGrid
            rows={campaigns || []}
            columns={campColumns}
            loading={campLoading}
            autoHeight
          />
        </DataTableCard>
      )}

      <Dialog open={campaignOpen} onClose={() => setCampaignOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create incentive campaign</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Title" fullWidth value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <TextField label="Description" fullWidth multiline rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            <TextField select label="Type" fullWidth value={form.campaignType} onChange={(e) => setForm({ ...form, campaignType: e.target.value })}>
              <MenuItem value="trip_target">Trip target</MenuItem>
              <MenuItem value="earnings_target">Earnings target</MenuItem>
              <MenuItem value="peak_hour">Peak hour bonus</MenuItem>
            </TextField>
            {form.campaignType === 'earnings_target' ? (
              <TextField label={fieldLabel('Target earnings')} type="number" fullWidth value={form.targetEarnings} onChange={(e) => setForm({ ...form, targetEarnings: Number(e.target.value) })} />
            ) : (
              <TextField label="Target trips" type="number" fullWidth value={form.targetTrips} onChange={(e) => setForm({ ...form, targetTrips: Number(e.target.value) })} />
            )}
            <TextField label={fieldLabel('Bonus amount')} type="number" fullWidth value={form.bonusAmount} onChange={(e) => setForm({ ...form, bonusAmount: Number(e.target.value) })} />
            <TextField label="Starts at" type="datetime-local" fullWidth value={form.startsAt} onChange={(e) => setForm({ ...form, startsAt: e.target.value })} InputLabelProps={{ shrink: true }} />
            <TextField label="Ends at" type="datetime-local" fullWidth value={form.endsAt} onChange={(e) => setForm({ ...form, endsAt: e.target.value })} InputLabelProps={{ shrink: true }} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCampaignOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!form.title || createCampaign.isPending} onClick={() => createCampaign.mutate()}>
            Create draft
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
}
